// @sentry/react is dynamically imported so its ~30 KB gzip stays out of
// the main chunk for users who haven't opted in to crash reporting.
// captureError is sync because ErrorBoundary.componentDidCatch can't await;
// if a crash hits before init resolves, we lose that one capture (still
// console.error'd in the boundary).

let initialized = false;
let mod: typeof import("@sentry/react") | null = null;

export async function initSentry(): Promise<void> {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn("[sentry] VITE_SENTRY_DSN not set; crash reporting disabled");
    return;
  }
  try {
    mod = await import("@sentry/react");
    mod.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    });
    initialized = true;
  } catch (err) {
    // Dynamic chunk blocked, CSP rejection, malformed DSN, or transport
    // refusing to start. Treat Sentry as best-effort: log + leave gate
    // closed so captureError short-circuits.
    console.error("[sentry] init failed; crash reporting disabled", err);
    mod = null;
  }
}

export function disableSentry(): void {
  // @sentry/react has no first-class shutdown that survives re-init in the
  // same page; flush + leave initialized=true until close resolves so a
  // crash in the 2-second flush window still captures, then flip the gate.
  if (initialized && mod) {
    const sentryMod = mod;
    sentryMod
      .close(2000)
      .catch((err) => console.warn("[sentry] close failed", err))
      .finally(() => {
        initialized = false;
        mod = null;
      });
  }
}

export function isSentryActive(): boolean {
  return initialized;
}

export function captureError(err: unknown): void {
  if (!initialized || !mod) return;
  try {
    mod.captureException(err);
  } catch {
    // Outer caller (ErrorBoundary.componentDidCatch) is already handling
    // an error. A throw here would double-fault React's error machinery.
  }
}
